import React, { useState, Fragment, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { H3_TITLE, QUANTITY } from '@Apis/constants';
import { Helmet } from 'react-helmet';
import H3Text from '@Uilib/h3Text';
import { notification, Checkbox } from 'antd';
import dayjs from 'dayjs';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import PurchaseOrderActions from '@Actions/purchaseOrderActions';
import SoActions from '@Actions/sales/soActions';
import TenantActions from '@Actions/tenantActions';
import InvoiceActions from '@Actions/invoiceActions';
import ReturnLines from './ReturnLines';
import './style.scss';
import Helpers from '@Apis/helpers';
import { v4 as uuidv4 } from 'uuid';

function ReturnForm({
  selectedLocationFulfillment, soMapping,
  callback, match, getInvoiceByIdLoading, getReturnFulfillmentLoading,
  createReturnFulfillmentLoading, returnFulfillments, selectedFulfillment, createReturnFulfillment,
  selectedSO, locationId, MONEY,
}) {
  const [trackUrl, setTrackUrl] = useState('');
  const [trackCompany, setTrackCompany] = useState('');
  const [trackNumber, setTrackNumber] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [toRestock, setToRestock] = useState(false);
  const [toRefund, setToRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [currentAction, setCurrentAction] = useState('');
  const [data, setData] = useState([]);

  const isCOD = !['paid', 'partially paid'].includes(selectedSO?.shopify_payment_status);

  useEffect(() => {
    if (returnFulfillments) {
      const selectedFulfillmentOrder = returnFulfillments?.data?.filter((item) => item?.shopify_fulfillment_id == selectedFulfillment?.shopifyFulfillmentId)?.[0];
      const returnLines = [];

      for (let i = 0; i < selectedFulfillmentOrder?.fulfillment_lines?.length; i++) {
        const returnLine = selectedFulfillmentOrder?.fulfillment_lines[i];

        const parentKey = uuidv4();

        returnLines.push({
          key: parentKey,
          product_sku_name: returnLine.so_line_object?.product_sku_name,
          product_sku_info: returnLine.so_line_object?.product_sku_info,
          shopify_fulfillment_line_item_id: returnLine.shopify_fulfillment_line_item_id,
          product_batches: returnLine?.so_line_object?.available_batches || [],
          selectedBatch: returnLine.fulfillment_line_info?.available_batches?.[0],
          ordered_quantity: returnLine.quantity,
          quantity: 0,
          customer_note: '',
          tax_info: returnLine.so_line_object?.tax_info,
          uom_info: returnLine.so_line_object?.uom_info,
          offer_price: returnLine.so_line_object?.offer_price,
          nextBatchCode: `${returnLine.so_line_object?.product_sku_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${returnLine.so_line_object?.product_sku_info?.product_batch_counter}`,
          tenant_product_id: returnLine.so_line_object?.tenant_product_id,
          bundle_products: returnLine?.bundle_products?.map((item) => ({
            ...item,
            parentKey,
            key: uuidv4(),
            unitPrice: QUANTITY(item?.unit_price),
            uom_info: item?.product_sku_info?.uom_info,
            product_sku_name: item?.product_sku_info?.product_sku_name,
            so_quantity: item?.quantity,
            expiry_days: item?.product_sku_info?.expiry_days,
            product_sku_info: item?.product_sku_info,
            product_batches: item?.available_batches || [],
            nextBatchCode: `${item?.product_sku_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${item?.product_sku_info?.product_batch_counter}`,
          })),
        });
      }
      setData(returnLines);
    }
  }, [returnFulfillments]);

  const handleDelete = (key) => {
    if (data?.length === 1) {
      notification.error({
        message: 'Order line must have atleast one line',
        placement: 'top',
        duration: 4,
      });
    } else {
      const copyData = data.filter((item) => item.key !== key);
      setData(copyData);
    }
  };

  const handleQuantityChange = (dataProp) => {
    if (!isCOD) {
      let total = 0;

      for (let i = 0; i < dataProp?.length; i++) {
        total += dataProp[i].quantity * dataProp[i]?.offer_price * (1 + (dataProp[i]?.tax_info?.tax_value) / 100);
      }
      setRefundAmount(total);
    } else {
      setRefundAmount(0);
    }
  };

  const isDataValid = (withRefund) => {
    let isDataValidValue = true;

    for (let i = 0; i < data?.length; i++) {
      const productType = data[i]?.product_sku_info?.product_type;
      if (productType === 'BUNDLE') {
        for (let j = 0; j < data[i]?.bundle_products?.length; j++) {
          if (Number(data[i].quantity) <= 0 || Number(data[i].quantity) > Number(data[i].ordered_quantity) || !data[i].customer_note || (toRestock && (!['SERVICE', 'NON_STORABLE']?.includes(data[i]?.bundle_products[j]?.product_sku_info?.product_type) ? !data[i]?.bundle_products[j]?.selectedBatch : false))) {
            isDataValidValue = false;
          }
        }
      } else if (Number(data[i].quantity) <= 0 || Number(data[i].quantity) > Number(data[i].ordered_quantity) || !data[i].customer_note || (toRestock && !data[i]?.selectedBatch)) {
        isDataValidValue = false;
      }
    }

    return isDataValidValue;
  };

  const hasTrackInfo = () => {
    let hasTrackInfoValue = true;
    if (trackNumber) {
      if (!trackUrl || !trackCompany) {
        hasTrackInfoValue = false;
      } else if (trackUrl && !Helpers.isValidUrl(trackUrl)) {
        hasTrackInfoValue = false;
      }
    }
    return hasTrackInfoValue;
  };

  const fulfillOrder = (withRefund) => {
    const { orderId } = match.params;
    setFormSubmitted(true);

    const returnLines = [];

    if (isDataValid(withRefund) && hasTrackInfo() && (toRefund ? refundAmount : true)) {
      for (let i = 0; i < data?.length; i++) {
        const returnLine = {
          shopify_fulfillment_line_item_id: data[i]?.shopify_fulfillment_line_item_id,
          quantity: data[i]?.quantity,
          customer_note: data[i]?.customer_note,
          restock: toRestock,
          location_id: selectedFulfillment?.shopify_location_id,
          product_batch: (data[i]?.product_sku_info?.product_type !== 'BUNDLE' && toRestock) ? data[i]?.product_batches?.map((item) => ({
            ...item,
            quantity: data[i]?.quantity,
            uom_id: data[i]?.uom_info?.[0]?.uom_id,
            tenant_product_id: data[i]?.tenant_product_id,
          })) : null,
          bundle_products: data[i]?.bundle_products?.map((item) => ({
            quantity: item?.quantity,
            restock: toRestock,
            product_sku_id: item.product_sku_info.product_sku_id,
            product_batch: toRestock ? item?.product_batches?.map((batch) => ({
              ...batch,
              quantity: item?.quantity,
              uom_id: item?.uom_id,
              product_sku_id: item.product_sku_info.product_sku_id,
            })) : null,
          })),
        };
        returnLines.push(returnLine);
      }
      const payload = {
        order_id: orderId,
        shopify_order_id: selectedSO?.shopify_order_id,
        return_lines: returnLines,
        shop_name: selectedFulfillment?.shopify_shop_name,
        notify_customer: !!notifyCustomer,
        tracking_info: {
          number: trackNumber,
          company: trackCompany,
          url: trackUrl,
        },
        status: withRefund ? 'COMPLETED' : 'IN_PROGRESS',
        refund_amount: toRefund ? refundAmount : null,
      };

      createReturnFulfillment(payload, () => {
        callback();
        setCurrentAction('');
      });
    }
  };

  const getDataSource = (dataValue) => {
    if (dataValue) {
      const newData = JSON.parse(JSON.stringify(dataValue));
      return newData;
    }
    return [];
  };

  const { orderId } = match.params;

  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{H3_TITLE}</title>
      </Helmet>
      {getInvoiceByIdLoading ? (
        <div className="fulfill__loading__wrapper">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <div key={item} className="fulfill__loading__wrapper-row">
              <div className="fulfill__loading__wrapper-row__label loadingBlock" />
              <div className="fulfill__loading__wrapper-row__value loadingBlock" />
            </div>
          ))}
          <div className="fulfill__loading__wrapper-table loadingBlock" />
          <div className="fulfill__loading__wrapper-details loadingBlock" />

        </div>
      )
        : (
          <div className={match.url.includes('/sales-order') ? 'fulfill__wrapper fulfill-component' : 'fulfill__wrapper fulfill-page'}>
            <div className="ant-row">
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="SO#" className="fulfill__input-row__label" />
                  <div style={{ marginTop: '5px', fontSize: '13px', fontWeight: '500' }} className="fulfill__input-row__input">
                    {orderId}
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="Shopify Order#" className="fulfill__input-row__label" />
                  <div style={{ marginTop: '5px', fontSize: '13px', fontWeight: '500' }} className="fulfill__input-row__input">
                    {selectedSO?.shopify_order_number || ''}
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row" style={{ marginBottom: '10px' }}>
                  <H3Text text="Location" className="fulfill__input-row__label" />
                  <div style={{ marginTop: '5px', fontSize: '13px', fontWeight: '500' }} className="fulfill__input-row__input">
                    {`${selectedFulfillment?.tenant_name} (${selectedFulfillment?.shopify_integration_details?.locations?.filter((item) => item?.id === Number(selectedFulfillment?.assigned_shopify_location_id))[0]?.name})`}
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="Tracking Number" className="fulfill__input-row__label" />
                  <H3FormInput
                    name="track number"
                    type="text"
                    containerClassName="orgInputContainer fulfill__input-row__input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput input"
                    placeholder=""
                    onChange={(e) => setTrackNumber(e.target.value)}
                    value={trackNumber}
                    disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading}
                  />
                </div>
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="Shipping Carrier" className="fulfill__input-row__label" />
                  <H3FormInput
                    name="track company"
                    type="text"
                    containerClassName="orgInputContainer fulfill__input-row__input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput input"
                    placeholder=""
                    onChange={(e) => setTrackCompany(e.target.value)}
                    value={trackCompany}
                    disabled={getReturnFulfillmentLoading || !trackNumber || createReturnFulfillmentLoading}
                  />
                </div>
                {formSubmitted && trackNumber && !trackCompany && <div className="input-error" style={{ marginBottom: (formSubmitted && trackNumber && !trackCompany) ? '10px' : '0px', marginLeft: '180px', marginTop: '-10px' }}>*Please enter shipping carrier</div>}
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="Tracking URL" className="fulfill__input-row__label" />
                  <H3FormInput
                    name="track url"
                    type="text"
                    containerClassName="orgInputContainer fulfill__input-row__input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput input"
                    placeholder=""
                    onChange={(e) => setTrackUrl(e.target.value)}
                    value={trackUrl}
                    disabled={getReturnFulfillmentLoading || !trackNumber || !trackCompany || createReturnFulfillmentLoading}
                  />
                </div>
                {formSubmitted && (trackNumber || trackCompany) && (!trackUrl || !Helpers.isValidUrl(trackUrl)) && <div className="input-error" style={{ marginBottom: (formSubmitted && (trackNumber || trackCompany) && (!trackUrl || !Helpers.isValidUrl(trackUrl))) ? '10px' : '0px', marginLeft: '180px', marginTop: '-10px' }}>*Please enter a valid tracking URL</div>}
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row">
                  <H3Text text="Shopify Payment Amount" className="fulfill__input-row__label" />
                  <div
                    style={{
                      marginTop: '5px', fontSize: '13px', fontWeight: '500', color: '#2D7DF7',
                    }}
                    className="fulfill__input-row__input"
                  >
                    {MONEY(selectedSO?.shopify_paid_amount)}
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" />
              <div className="ant-col-md-12">
                <div className="fulfill__input-row" style={{ marginTop: '5px' }}>
                  <H3Text text="Notify customer" className="fulfill__input-row__label" />
                  <div style={{ marginTop: '5px' }} className="fulfill__input-row__input">
                    <Checkbox
                      disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading}
                      checked={notifyCustomer}
                      onChange={(event) => {
                        setNotifyCustomer(event.target.checked);
                      }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '12px', marginLeft: '5px' }}>
                      Send shipment details to your customer now
                    </span>
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" />

              {/* <div className="ant-col-md-12">
                <div className="fulfill__input-row" style={{ marginTop: '7px' }}>
                  <H3Text text="Restock" className="fulfill__input-row__label" />
                  <div className="fulfill__input-row__input">
                    <Checkbox
                      disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading}
                      checked={toRestock}
                      onChange={(event) => {
                        setToRestock(event.target.checked);
                        setRefundAmount('');
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="ant-col-md-12" /> */}
              {/*{selectedSO?.shopify_payment_status === 'paid'*/}
              {/*&& (*/}
              {/*  <div className="ant-col-md-12">*/}
              {/*    <div className="fulfill__input-row" style={{ marginTop: '7px' }}>*/}
              {/*      <H3Text text="Request Refund" className="fulfill__input-row__label" />*/}
              {/*      <div>*/}
              {/*        <Checkbox*/}
              {/*          disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading || isCOD}*/}
              {/*          checked={toRefund}*/}
              {/*          onChange={(event) => {*/}
              {/*            setToRefund(event.target.checked);*/}
              {/*            setRefundAmount('');*/}
              {/*          }}*/}
              {/*        />*/}
              {/*      </div>*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*)}*/}
              {/*{(toRefund && !isCOD) && (*/}
              {/*  <div className="ant-col-md-12">*/}
              {/*    <div className="fulfill__input-row" style={{ marginTop: '7px' }}>*/}
              {/*      <H3Text text="Refund Amount" className="fulfill__input-row__label" />*/}
              {/*      <H3FormInput*/}
              {/*        name="refund amount"*/}
              {/*        type="text"*/}
              {/*        containerClassName="orgInputContainer fulfill__input-row__input"*/}
              {/*        labelClassName="orgFormLabel"*/}
              {/*        inputClassName="orgFormInput input"*/}
              {/*        placeholder=""*/}
              {/*        onChange={(e) => setRefundAmount(e.target.value)}*/}
              {/*        value={refundAmount}*/}
              {/*        disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading}*/}
              {/*      />*/}
              {/*    </div>*/}
              {/*    {formSubmitted && toRefund && !isCOD && !refundAmount && <div className="input-error" style={{ marginBottom: (formSubmitted && toRefund && !isCOD && !refundAmount) ? '10px' : '0px', marginLeft: '180px', marginTop: '-10px' }}>*Please enter refund amount</div>}*/}
              {/*  </div>*/}
              {/*)}*/}

              <div className="ant-col-md-12" />
            </div>
            <div className="fulfill__lines-wrapper">
              <ReturnLines
                handleDelete={handleDelete}
                data={getDataSource(data)}
                handleQuantityChange={handleQuantityChange}
                updateData={(updatedData) => setData(updatedData)}
                formSubmitted={formSubmitted}
                loading={getReturnFulfillmentLoading || createReturnFulfillmentLoading}
                selectedLocationFulfillment={selectedLocationFulfillment}
                soMapping={soMapping}
                toRestock={toRestock}
                toRefund={toRefund}
                currentAction={currentAction}
                isCOD={isCOD}
              />
            </div>
          </div>
        )}
      <div className="fulfill__footer">
        {!toRestock && (
          <H3Button
            text="Create Return"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="fulfill__footer-submit-left"
            onClick={() => {
              setCurrentAction('IN_PROGRESS');
              fulfillOrder(false);
            }}
            isLoading={currentAction === 'IN_PROGRESS' && (getReturnFulfillmentLoading || createReturnFulfillmentLoading)}
            disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading || toRestock || toRefund}
          />
        )}
        {toRestock
          && (
            <H3Button
              text="Create Return and Restock"
              buttonType={defaultButtonTypes.BLUE_ROUNDED}
              customClass="fulfill__footer-submit-right"
              onClick={() => {
                setCurrentAction('COMPLETED');
                fulfillOrder(true);
              }}
              isLoading={currentAction === 'COMPLETED' && (getReturnFulfillmentLoading || createReturnFulfillmentLoading)}
              disabled={getReturnFulfillmentLoading || createReturnFulfillmentLoading}
            />
          )}
      </div>
    </Fragment>
  );
}

const mapStateToProps = ({
  SellerReducers, UserReducers, ProductReducers, SOReducers, TenantReducers, InvoiceReducers,
}) => ({
  MONEY: UserReducers.MONEY,
  sellers: SellerReducers.sellers,
  products: ProductReducers.products,
  tenantsConfiguration: TenantReducers.tenantsConfiguration,
  user: UserReducers.user,
  selectedInvoice: InvoiceReducers.selectedInvoice,
  getReturnFulfillmentLoading: SOReducers.getReturnFulfillmentLoading,
  returnFulfillments: SOReducers.returnFulfillments,
  createReturnFulfillmentLoading: SOReducers.createReturnFulfillmentLoading,
  selectedSO: SOReducers.selectedSO,
});

const mapDispatchToProps = (dispatch) => ({
  updatePoStatus: (payload, callback) => dispatch(PurchaseOrderActions.updatePoStatus(payload, callback)),
  getTenantsConfiguration: (tenantId, callback) => dispatch(TenantActions.getTenantsConfiguration(tenantId, callback)),
  getInvoiceById: (tenantId, invoiceId) => dispatch(InvoiceActions.getInvoiceById(tenantId, invoiceId)),
  updateInvoice: (payload, callback) => dispatch(InvoiceActions.updateInvoice(payload, callback)),
  fulfillUnfulfilledOrder: (payload, callback) => dispatch(SoActions.fulfillUnfulfilledOrder(payload, callback)),
  getReturnFulfillment: (orderId) => dispatch(SoActions.getReturnFulfillment(orderId)),
  createReturnFulfillment: (payload, callback) => dispatch(SoActions.createReturnFulfillment(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ReturnForm));
